import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiService from '../../services/api';
import { AuthTokens, LoginRequest, RegisterRequest, User } from '../../types';

interface UserState {
  user: User | null;
  tokens: AuthTokens | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  error: string | null;
}

// Initialize from localStorage
const getInitialState = (): UserState => {
  try {
    const storedTokens = localStorage.getItem('auth_tokens');
    const storedUser = localStorage.getItem('auth_user');

    let tokens: AuthTokens | null = null;
    let user: User | null = null;

    if (storedTokens) {
      try {
        const parsed = JSON.parse(storedTokens);
        if (parsed && parsed.accessToken) {
          tokens = parsed;
        }
      } catch (e) {
        console.warn('Failed to parse stored tokens:', e);
      }
    }

    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        if (parsed && parsed._id) {
          user = parsed;
        }
      } catch (e) {
        console.warn('Failed to parse stored user:', e);
      }
    }

    // If we have both user and tokens, we're already authenticated
    // Set isLoading to false immediately since we have the data
    const hasAuth = !!(user && tokens);
    return {
      user,
      tokens,
      isLoggedIn: hasAuth,
      isLoading: !hasAuth, // Only loading if we don't have auth data
      error: null,
    };
  } catch (e) {
    return {
      user: null,
      tokens: null,
      isLoggedIn: false,
      isLoading: true,
      error: null,
    };
  }
};

const initialState: UserState = getInitialState();

// Async thunks
export const initializeAuth = createAsyncThunk(
  'user/initializeAuth',
  async (_, { dispatch, getState }) => {
    const rootState = getState() as { user: UserState };
    const storedTokens = localStorage.getItem('auth_tokens');
    const storedUser = localStorage.getItem('auth_user');

    // If we already have valid auth in state (from initial state), just verify in background
    if (rootState.user.isLoggedIn && rootState.user.user && rootState.user.tokens) {
      // Verify with API in background (non-blocking) to update user data if needed
      apiService
        .getCurrentUser()
        .then(currentUser => {
          if (currentUser) {
            localStorage.setItem('auth_user', JSON.stringify(currentUser));
            // Update user in store
            dispatch(setUser(currentUser));
          }
        })
        .catch(() => {
          // Silently fail - we have stored user data, keep them logged in
        });

      // Return existing state - don't change anything
      return { user: rootState.user.user, tokens: rootState.user.tokens };
    }

    // If we have stored data but state wasn't initialized properly
    if (storedTokens && storedUser) {
      try {
        const parsedTokens = JSON.parse(storedTokens);
        const parsedUser = JSON.parse(storedUser);

        if (parsedTokens && parsedTokens.accessToken && parsedUser && parsedUser._id) {
          // Verify with API in background (non-blocking)
          apiService
            .getCurrentUser()
            .then(currentUser => {
              if (currentUser) {
                localStorage.setItem('auth_user', JSON.stringify(currentUser));
                // Update user in store
                dispatch(setUser(currentUser));
              }
            })
            .catch(() => {
              // Silently fail - we have stored user data
            });

          return { user: parsedUser, tokens: parsedTokens };
        }
      } catch (e) {
        console.warn('Failed to parse stored auth data:', e);
        // Don't throw - return null values but don't clear localStorage
        return { user: null, tokens: null };
      }
    }

    // Try to get from API if no stored user but have tokens
    if (storedTokens && !storedUser) {
      try {
        const parsedTokens = JSON.parse(storedTokens);
        if (parsedTokens && parsedTokens.accessToken) {
          const currentUser = await apiService.getCurrentUser();
          if (currentUser) {
            localStorage.setItem('auth_user', JSON.stringify(currentUser));
            return { user: currentUser, tokens: parsedTokens };
          }
        }
      } catch (e) {
        // API call failed - but don't clear tokens if we have them
        // Just return null and let the state stay as is
        console.warn('Failed to get user from API:', e);
        return { user: null, tokens: null };
      }
    }

    // No stored data - return null
    return { user: null, tokens: null };
  }
);

export const loginUser = createAsyncThunk(
  'user/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const authResponse = await apiService.login(credentials);
      localStorage.setItem('auth_tokens', JSON.stringify(authResponse.tokens));
      localStorage.setItem('auth_user', JSON.stringify(authResponse.user));
      return authResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'user/register',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      const authResponse = await apiService.register(userData);
      localStorage.setItem('auth_tokens', JSON.stringify(authResponse.tokens));
      localStorage.setItem('auth_user', JSON.stringify(authResponse.user));
      return authResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const logoutUser = createAsyncThunk('user/logout', async () => {
  try {
    await apiService.logout();
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('auth_user');
  }
});

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isLoggedIn = !!(action.payload && state.tokens);
    },
    setTokens: (state, action: PayloadAction<AuthTokens | null>) => {
      state.tokens = action.payload;
      state.isLoggedIn = !!(state.user && action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearAuth: state => {
      state.user = null;
      state.tokens = null;
      state.isLoggedIn = false;
      state.error = null;
      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('auth_user');
    },
  },
  extraReducers: builder => {
    builder
      // Initialize auth
      .addCase(initializeAuth.pending, state => {
        // Only set loading if we don't already have auth
        if (!state.isLoggedIn) {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        // Only update state if we got valid data, or if we don't already have valid state
        // This prevents clearing valid localStorage data
        if (action.payload.user && action.payload.tokens) {
          state.user = action.payload.user;
          state.tokens = action.payload.tokens;
          state.isLoggedIn = true;
        } else if (!state.isLoggedIn) {
          // Only clear if we don't already have valid auth
          state.user = action.payload.user;
          state.tokens = action.payload.tokens;
          state.isLoggedIn = false;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        // Never clear state on rejection - we might have valid localStorage data
        // Only set loading to false
        console.warn('initializeAuth rejected:', action.error);
        state.isLoading = false;
        state.error = null;
        // Keep existing state - don't clear user/tokens
      })
      // Login
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isLoggedIn = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.user = null;
        state.tokens = null;
        state.isLoggedIn = false;
        state.isLoading = false;
        state.error = (action.payload as string) || 'Login failed';
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('auth_user');
      })
      // Register
      .addCase(registerUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isLoggedIn = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.user = null;
        state.tokens = null;
        state.isLoggedIn = false;
        state.isLoading = false;
        state.error = (action.payload as string) || 'Registration failed';
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('auth_user');
      })
      // Logout
      .addCase(logoutUser.pending, state => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, state => {
        state.user = null;
        state.tokens = null;
        state.isLoggedIn = false;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, state => {
        state.user = null;
        state.tokens = null;
        state.isLoggedIn = false;
        state.isLoading = false;
        state.error = null;
      });
  },
});

export const { setUser, setTokens, setLoading, setError, clearAuth } = userSlice.actions;
export default userSlice.reducer;
